/**
 * Contrato do RH doméstico: colaboradores e banco de horas.
 *
 * Papel: aqui ficam `STATUS_COLABORADOR`, de onde nasce o enum
 * `status_colaborador` do Postgres, as constantes do banco de horas —
 * `MINUTOS_POR_DIA` e `LIMIAR_DE_ALERTA_EM_MINUTOS` —, as funções
 * `saldoEmMinutos`, `faixaDoSaldo` e `ultimoDiaDaCompetencia`, e os schemas que
 * os formulários e as Functions usam: os do quadro de colaboradores e os do
 * banco de horas, que tem lista própria de colaboradores, separada do quadro.
 *
 * Como usar: o saldo **não é campo**. É `Σ (credito − debito + ajuste)` sobre os
 * meses, e quem o calcula é `saldoEmMinutos`, aqui, para a tela e qualquer
 * relatório somarem do mesmo jeito. Guardá-lo numa coluna criaria um segundo
 * lugar onde a mesma verdade estaria escrita, e os dois divergiriam no primeiro
 * mês corrigido.
 *
 * O tempo trafega e é guardado em **minutos inteiros**. `HH:MM` é forma de
 * escrever, e quem a converte é `minutosSchema`, na entrada de
 * `novoLancamentoMensalSchema`; qualquer decimal em horas reintroduz erro de
 * arredondamento a cada soma de um ano de fechamentos.
 */
import { z } from 'zod';

import { dataOpcionalSchema, idSchema, textoObrigatorio, textoOpcional } from './comum';
import { competenciaSchema, cpfSchema, dinheiroOpcionalSchema } from './formatos';

/**
 * Quantos minutos tem um dia de trabalho.
 *
 * É o divisor que transforma o saldo em dias, que é como o saldo se lê: um
 * número em minutos não diz nada a quem precisa decidir uma folga.
 */
export const MINUTOS_POR_DIA = 480;

/**
 * A partir de que saldo negativo a situação deixa de ser normal.
 *
 * São nove dias de trabalho em débito. Abaixo disso a tela marca em vermelho —
 * é o ponto em que o acúmulo deixa de se resolver com uma folga e passa a
 * precisar de acordo.
 */
export const LIMIAR_DE_ALERTA_EM_MINUTOS = -MINUTOS_POR_DIA * 9;

/** Em que situação o colaborador está. */
export const STATUS_COLABORADOR = ['ativo', 'desligado', 'diarista'] as const;

/**
 * Em que situação o colaborador está.
 *
 * `diarista` descreve o vínculo e não a situação — quem é diarista está
 * trabalhando —, e isso é imperfeito de propósito: separar em duas colunas
 * exigiria decidir se a diarista conta como ativa em cada contagem do sistema,
 * e essa é resposta de quem administra, não de quem modela.
 */
export const statusColaboradorSchema = z.enum(STATUS_COLABORADOR, {
  message: 'Situação desconhecida.',
});

export type StatusColaborador = z.infer<typeof statusColaboradorSchema>;

/**
 * Um mês de banco de horas, no mínimo que o saldo precisa.
 *
 * `saldoEmMinutos` aceita qualquer objeto com os três campos, e não só o que a
 * API devolve: assim o migrador, a reconciliação e a tela somam pela mesma
 * função, cada um com a forma que tem em mãos.
 */
export interface MesComMinutos {
  creditoMin: number;
  debitoMin: number;
  ajusteMin: number;
}

/**
 * Soma o saldo de banco de horas de uma lista de meses.
 *
 * O ajuste entra somando porque é o que corrige o que a marcação de ponto não
 * pegou — acordo, fechamento de período, acerto de férias. Um ajuste negativo
 * reduz o saldo, e é assim que se lança um débito reconhecido fora do ponto.
 *
 * @param meses - Os meses do colaborador, em qualquer ordem.
 * @returns O saldo em minutos; negativo quando o débito supera o crédito.
 */
export function saldoEmMinutos(meses: readonly MesComMinutos[]): number {
  return meses.reduce((soma, mes) => soma + mes.creditoMin - mes.debitoMin + mes.ajusteMin, 0);
}

/**
 * Traduz um saldo em minutos na faixa em que ele está.
 *
 * São três, e o `ResumoBancoHoras` dá a cada uma a variante do selo, por
 * `VARIANTE_DA_FAIXA`: `positivo` quando o
 * colaborador tem horas a receber, `atencao` no débito que ainda se resolve com
 * folga, e `critico` abaixo de `LIMIAR_DE_ALERTA_EM_MINUTOS`.
 *
 * @param minutos - O saldo, como `saldoEmMinutos` o devolve.
 * @returns A faixa, para a tela escolher o tom.
 */
export function faixaDoSaldo(minutos: number): 'positivo' | 'atencao' | 'critico' {
  if (minutos >= 0) return 'positivo';
  return minutos >= LIMIAR_DE_ALERTA_EM_MINUTOS ? 'atencao' : 'critico';
}

/**
 * O que o formulário envia para criar ou alterar um colaborador.
 *
 * `cpf` é obrigatório e único: é a chave de reconciliação de pessoas do sistema
 * inteiro — é por ele que o favorecido de Finanças se liga ao colaborador — e
 * um cadastro sem ele nasce impossível de reconciliar.
 *
 * `apelido` aceita ausência porque nem todo colaborador tem um registrado, e
 * inventá-lo a partir do primeiro nome produziria dois apelidos iguais entre
 * pessoas de mesmo primeiro nome — que é justamente o que faz a escala do
 * calendário deixar de casar.
 *
 * `salario` e `admissao` aceitam ausência porque quem presta serviço por diária
 * não tem nem um nem outro.
 */
export const novoColaboradorSchema = z
  .object({
    nomeCompleto: textoObrigatorio('Nome completo'),
    apelido: textoOpcional(40),
    cpf: cpfSchema,
    cargo: textoObrigatorio('Cargo', 80),
    departamento: textoOpcional(80),
    residenciaId: idSchema,
    status: statusColaboradorSchema,
    admissao: dataOpcionalSchema,
    desligamento: dataOpcionalSchema,
    salario: dinheiroOpcionalSchema,
    observacao: textoOpcional(2000),
  })
  .refine(
    (colaborador) =>
      colaborador.admissao === null ||
      colaborador.desligamento === null ||
      colaborador.desligamento >= colaborador.admissao,
    { message: 'O desligamento não pode ser anterior à admissão.', path: ['desligamento'] },
  );

export type NovoColaborador = z.infer<typeof novoColaboradorSchema>;

/** A residência onde o colaborador trabalha, como a listagem a traz aninhada. */
export const residenciaDoColaboradorSchema = z.object({
  id: idSchema,
  nome: z.string(),
  apelido: z.string(),
});

/** Um colaborador como a API o devolve. */
export const colaboradorSchema = z.object({
  id: idSchema,
  nomeCompleto: z.string(),
  apelido: z.string().nullable(),
  cpf: z.string(),
  cargo: z.string(),
  departamento: z.string().nullable(),
  residencia: residenciaDoColaboradorSchema,
  status: statusColaboradorSchema,
  admissao: z.string().nullable(),
  desligamento: z.string().nullable(),
  salario: z.number().nullable(),
  observacao: z.string().nullable(),
  criadoEm: z.iso.datetime({ message: 'Instante inválido.' }),
  atualizadoEm: z.iso.datetime({ message: 'Instante inválido.' }),
});

export type Colaborador = z.infer<typeof colaboradorSchema>;

/**
 * Minutos de um fechamento mensal, aceitos como número ou como `HH:MM`.
 *
 * O campo da tela entrega `"12:30"` e quem chama a API direto costuma mandar
 * `750`. As duas formas chegam aqui, e o que sai é sempre o inteiro.
 *
 * O sinal negativo é aceito no ajuste, que é onde ele faz sentido; crédito e
 * débito negativos são recusados pelo `refine` de quem os usa.
 *
 * A vírgula é lida como o separador de `HH:MM`, nunca como decimal: `"12,30"`
 * vale os mesmos 750 minutos de `"12:30"`. Quem escrever `"1,5"` esperando hora
 * e meia é recusado pelo `refine`, porque `1:5` não tem os dois dígitos de
 * minuto que o padrão exige — e a recusa é o que impede os 90 minutos
 * pretendidos entrarem como 65 sem ninguém perceber.
 */
const minutosSchema = z
  .union([z.number(), z.string()])
  .nullish()
  .transform((valor) => {
    if (valor == null) return 0;
    if (typeof valor === 'number') return valor;

    const texto = valor.trim().replace(',', ':');
    if (texto === '') return 0;

    const negativo = texto.startsWith('-');
    const partes = /^-?(\d{1,4}):(\d{2})$/.exec(texto);
    if (partes === null) return Number(texto);

    const horas = Number(partes[1]);
    const minutos = Number(partes[2]);
    return (negativo ? -1 : 1) * (horas * 60 + minutos);
  })
  .refine((valor) => Number.isInteger(valor), {
    message: 'Informe o tempo como HH:MM ou em minutos inteiros.',
  });

/**
 * O último dia do mês de uma competência, em ISO.
 *
 * É a data que a importação do extrato grava em "atualizado até": o extrato
 * fecha o mês inteiro, e o resumo diz até que dia as horas estão lançadas.
 * Fevereiro sai com 29 nos anos bissextos.
 *
 * @param competencia - `AAAA-MM`, já validada.
 * @returns `AAAA-MM-DD` do último dia do mês.
 */
export function ultimoDiaDaCompetencia(competencia: string): string {
  const [ano = 0, mes = 0] = competencia.split('-').map(Number);
  // O dia 0 do mês seguinte é o último dia do mês pedido.
  const dias = new Date(Date.UTC(ano, mes, 0)).getUTCDate();
  return `${competencia}-${String(dias).padStart(2, '0')}`;
}

/**
 * O período de um lançamento mensal, com a mensagem que a caixa do mês mostra.
 *
 * É a mesma forma de `competenciaSchema`, com o texto que a origem escreve no
 * `alert()` de `bhSaveEntry`.
 */
const periodoSchema = z
  .string()
  .trim()
  .regex(/^\d{4}-(0[1-9]|1[0-2])$/, { message: 'Informe o período no formato AAAA-MM.' });

/**
 * O que a caixa de colaborador do banco de horas envia.
 *
 * A lista do banco de horas é **separada** do quadro de colaboradores: quem
 * está aqui não precisa de CPF nem de casa, e mudar alguém aqui não muda o
 * quadro. O apelido vazio vira o primeiro nome do `nomeCompleto`, e função,
 * departamento e admissão vazios viram nulos — a tela escreve `—` no lugar.
 */
export const novoColaboradorBancoHorasSchema = z
  .object({
    nomeCompleto: z
      .string()
      .trim()
      .min(1, { message: 'Informe o nome do colaborador.' })
      .max(200, { message: 'O nome deve ter no máximo 200 caracteres.' }),
    apelido: textoOpcional(40),
    funcao: textoOpcional(80),
    departamento: textoOpcional(80),
    admissao: dataOpcionalSchema,
    ativo: z.boolean(),
  })
  .transform((colaborador) => ({
    ...colaborador,
    apelido:
      colaborador.apelido ??
      colaborador.nomeCompleto.split(' ')[0] ??
      colaborador.nomeCompleto,
  }));

export type NovoColaboradorBancoHoras = z.infer<typeof novoColaboradorBancoHorasSchema>;

/**
 * O que a caixa do mês envia para criar ou alterar um lançamento mensal.
 *
 * `colaboradorId` é o da lista do banco de horas. Os três tempos chegam em
 * `HH:MM` ou em minutos e saem em minutos inteiros; crédito e débito recusam
 * negativo e o ajuste aceita.
 */
export const novoLancamentoMensalSchema = z
  .object({
    colaboradorId: idSchema,
    competencia: periodoSchema,
    creditoMin: minutosSchema,
    debitoMin: minutosSchema,
    ajusteMin: minutosSchema,
    observacao: textoOpcional(500),
  })
  .refine((mes) => mes.creditoMin >= 0, {
    message: 'O crédito não pode ser negativo; use o débito.',
    path: ['creditoMin'],
  })
  .refine((mes) => mes.debitoMin >= 0, {
    message: 'O débito não pode ser negativo; use o crédito.',
    path: ['debitoMin'],
  });

export type NovoLancamentoMensal = z.infer<typeof novoLancamentoMensalSchema>;

/** Um lançamento mensal como a API o devolve, ligado ao colaborador pelo `id`. */
export const lancamentoMensalSchema = z.object({
  id: idSchema,
  colaboradorId: idSchema,
  competencia: competenciaSchema,
  creditoMin: z.number().int(),
  debitoMin: z.number().int(),
  ajusteMin: z.number().int(),
  observacao: z.string().nullable(),
  criadoEm: z.iso.datetime({ message: 'Instante inválido.' }),
  atualizadoEm: z.iso.datetime({ message: 'Instante inválido.' }),
});

export type LancamentoMensal = z.infer<typeof lancamentoMensalSchema>;

/**
 * Um colaborador do banco de horas como a API o devolve, com os meses dele.
 *
 * `meses` vem em ordem crescente de competência, que é a ordem em que o saldo
 * acumulado se lê.
 */
export const colaboradorBancoHorasSchema = z.object({
  id: idSchema,
  nomeCompleto: z.string(),
  apelido: z.string(),
  funcao: z.string().nullable(),
  departamento: z.string().nullable(),
  admissao: z.string().nullable(),
  ativo: z.boolean(),
  meses: z.array(lancamentoMensalSchema),
  criadoEm: z.iso.datetime({ message: 'Instante inválido.' }),
  atualizadoEm: z.iso.datetime({ message: 'Instante inválido.' }),
});

export type ColaboradorBancoHoras = z.infer<typeof colaboradorBancoHorasSchema>;

/**
 * O banco de horas inteiro, numa leitura só.
 *
 * `atualizacao` é o "ATUALIZADO ATÉ" do resumo, e só a importação do extrato a
 * muda; `null` quando nenhuma importação ou migração a gravou.
 */
export const bancoDeHorasSchema = z.object({
  atualizacao: z.string().nullable(),
  colaboradores: z.array(colaboradorBancoHorasSchema),
});

export type BancoDeHoras = z.infer<typeof bancoDeHorasSchema>;

/** Uma linha do extrato já casada com o colaborador, como a prévia a envia. */
export const linhaImportadaBancoHorasSchema = z.object({
  colaboradorId: idSchema,
  competencia: competenciaSchema,
  creditoMin: z
    .number()
    .int()
    .min(0, { message: 'O crédito não pode ser negativo; use o débito.' }),
  debitoMin: z
    .number()
    .int()
    .min(0, { message: 'O débito não pode ser negativo; use o crédito.' }),
  ajusteMin: z.number().int(),
});

export type LinhaImportadaBancoHoras = z.infer<typeof linhaImportadaBancoHorasSchema>;

/**
 * O que "Aplicar" envia: as linhas novas e as que mudam.
 *
 * Cada pessoa aparece no máximo uma vez por mês. A prévia já deixa só a última
 * linha de cada par, e o par repetido aqui seria gravado duas vezes no mesmo
 * lote, com o resultado dependendo da ordem.
 */
export const importacaoBancoHorasSchema = z
  .object({
    linhas: z
      .array(linhaImportadaBancoHorasSchema)
      .min(1, { message: 'Nenhum registro para aplicar.' }),
  })
  .refine(
    (importacao) =>
      new Set(importacao.linhas.map((linha) => `${linha.colaboradorId}|${linha.competencia}`))
        .size === importacao.linhas.length,
    { message: 'A importação repete o mesmo mês para a mesma pessoa.', path: ['linhas'] },
  );

export type ImportacaoBancoHoras = z.infer<typeof importacaoBancoHorasSchema>;

/** Quantas linhas a importação gravou. */
export const resultadoImportacaoBancoHorasSchema = z.object({
  aplicados: z.number().int(),
});

export type ResultadoImportacaoBancoHoras = z.infer<
  typeof resultadoImportacaoBancoHorasSchema
>;
